import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Share2, ExternalLink, X, Send, Trash2 } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const TWITCH_PARENT = window.location.hostname;

// ── Comment Drawer ──────────────────────────────────────────────────────────────

const CommentDrawer = ({ clipId, authToken, onClose, onCommentsChange }) => {
  const [comments, setComments]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [body, setBody]             = useState('');
  const [posting, setPosting]       = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const listRef                     = useRef(null);

  const headers = authToken
    ? { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!clipId) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/highlights/${encodeURIComponent(clipId)}/comments`)
      .then(r => r.json())
      .then(d => setComments(d.comments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clipId]);

  const scrollToBottom = () => {
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 50);
  };

  const handlePost = async () => {
    if (!authToken || !body.trim() || posting) return;
    setPosting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/highlights/${encodeURIComponent(clipId)}/comments`, {
        method: 'POST', headers,
        body: JSON.stringify({ body: body.trim() }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.comment) {
        setComments(prev => {
          const updated = [...prev, data.comment];
          onCommentsChange?.(updated);
          return updated;
        });
        scrollToBottom();
      }
      setBody('');
    } catch { /* silent */ }
    finally { setPosting(false); }
  };

  const handleDelete = async (commentId) => {
    if (!authToken || deleting) return;
    setDeleting(commentId);
    try {
      await fetch(`${BACKEND_URL}/api/highlights/comments/${commentId}`, { method: 'DELETE', headers });
      setComments(prev => {
        const updated = prev.filter(c => c.id !== commentId);
        onCommentsChange?.(updated);
        return updated;
      });
    } catch { /* silent */ }
    finally { setDeleting(null); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60)   return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100 }}
      />

      {/* Drawer */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 101,
        background: '#0a0a0a', borderTop: '1px solid #1a1a1a',
        borderRadius: '16px 16px 0 0',
        height: '65vh', display: 'flex', flexDirection: 'column',
        animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
      }}>
        <style>{`
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
          @keyframes fadein  { from { opacity: 0; } to { opacity: 1; } }
          .comment-input:focus { outline: none; border-color: #333 !important; }
          .comment-avatar { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
        `}</style>

        {/* Handle + header */}
        <div style={{ padding: '12px 20px 10px', borderBottom: '1px solid #111', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#222', margin: '0 auto' }} />
            <span style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 900, fontSize: '1.1rem', letterSpacing: '-0.01em', color: '#fff' }}>
              {comments.length > 0 ? `${comments.length} COMMENTS` : 'COMMENTS'}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {/* Comment list */}
        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {loading && (
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6rem', color: '#333', padding: '1rem 0', letterSpacing: '0.1em' }}>
              LOADING...
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6rem', color: '#333', padding: '2rem 0', letterSpacing: '0.1em', textAlign: 'center' }}>
              NO COMMENTS YET. BE THE FIRST.
            </div>
          )}
          {comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 18, animation: 'fadein 0.2s ease' }}>
              {c.avatar_url
                ? <img src={c.avatar_url} alt="" className="comment-avatar" onError={e => e.target.style.display='none'} />
                : <div className="comment-avatar" style={{ background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5rem', color: '#444' }}>
                      {(c.username || '?')[0].toUpperCase()}
                    </span>
                  </div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#ddd' }}>
                    {c.username}
                  </span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5rem', color: '#333' }}>
                    {formatTime(c.created_at)}
                  </span>
                </div>
                <div style={{ fontFamily: '"Barlow", sans-serif', fontWeight: 300, fontSize: '0.82rem', color: '#aaa', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {c.body}
                </div>
              </div>
              {authToken && c.user_id && (
                <button
                  onClick={() => handleDelete(c.id)}
                  disabled={deleting === c.id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2a2a2a', alignSelf: 'flex-start', padding: '2px', flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ff4444'}
                  onMouseLeave={e => e.currentTarget.style.color = '#2a2a2a'}
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Input area */}
        <div style={{ borderTop: '1px solid #111', padding: '10px 16px', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          {authToken ? (
            <>
              <input
                className="comment-input"
                value={body}
                onChange={e => setBody(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handlePost()}
                placeholder="Add a comment..."
                maxLength={500}
                style={{
                  flex: 1, background: '#111', border: '1px solid #1a1a1a',
                  color: '#fff', padding: '0.5rem 0.85rem',
                  fontFamily: '"Barlow", sans-serif', fontSize: '0.82rem',
                  borderRadius: 4,
                }}
              />
              <button
                onClick={handlePost}
                disabled={posting || !body.trim()}
                style={{
                  background: body.trim() ? '#fff' : '#111', border: '1px solid #1a1a1a',
                  color: body.trim() ? '#000' : '#333',
                  width: 36, height: 36, borderRadius: 4, cursor: body.trim() ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                <Send size={14} />
              </button>
            </>
          ) : (
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6rem', color: '#444', letterSpacing: '0.08em', padding: '0.5rem 0' }}>
              LOG IN TO COMMENT
            </div>
          )}
        </div>
      </div>
    </>
  );
};


// ── Clip Card ───────────────────────────────────────────────────────────────────

const ClipCard = ({ clip, isActive, authToken, onOpenComments }) => {
  const [liked, setLiked]         = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [liking, setLiking]       = useState(false);
  const [comments, setComments]   = useState([]);
  const [shared, setShared]       = useState(false);

  // Fetch initial reaction state + recent comments
  useEffect(() => {
    if (!clip.clip_id) return;
    const url = `${BACKEND_URL}/api/highlights/${encodeURIComponent(clip.clip_id)}/reactions`;
    const opts = authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {};
    fetch(url, opts)
      .then(r => r.json())
      .then(d => { setLiked(d.liked); setLikeCount(d.count || 0); })
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/highlights/${encodeURIComponent(clip.clip_id)}/comments`)
      .then(r => r.json())
      .then(d => setComments(d.comments || []))
      .catch(() => {});
  }, [clip.clip_id, authToken]);

  const handleLike = async () => {
    if (!authToken || liking) return;
    // Optimistic update
    setLiked(l => !l);
    setLikeCount(c => liked ? Math.max(0, c - 1) : c + 1);
    setLiking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/highlights/${encodeURIComponent(clip.clip_id)}/react`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      setLiked(data.liked);
      setLikeCount(data.count || 0);
    } catch {
      // Revert on failure
      setLiked(l => !l);
      setLikeCount(c => liked ? c + 1 : Math.max(0, c - 1));
    } finally { setLiking(false); }
  };

  const handleShare = async () => {
    const url = clip.clip_url || `https://clips.twitch.tv/${clip.clip_id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: clip.title || 'BGN Highlights', url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch { /* cancelled */ }
  };

  // Key changes when isActive flips true — forces iframe remount so autoplay fires
  const iframeSrc = clip.clip_id
    ? `https://clips.twitch.tv/embed?clip=${clip.clip_id}&parent=${TWITCH_PARENT}&autoplay=true&muted=true`
    : null;

  return (
    <div style={{
      height: 'calc(100vh - 110px)', width: '100%', position: 'relative',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#000', scrollSnapAlign: 'start', flexShrink: 0,
    }}>
      {/* Clip embed — remounted when clip becomes active so autoplay triggers */}
      {iframeSrc ? (
        <iframe
          key={isActive ? `${clip.clip_id}-active` : `${clip.clip_id}-idle`}
          src={isActive ? iframeSrc : 'about:blank'}
          title={clip.title || 'BGN clip'}
          allowFullScreen
          allow="autoplay"
          style={{
            width: '100%', maxWidth: 960,
            height: '56.25vw', maxHeight: 'calc(100vh - 130px)',
            border: 'none', display: 'block',
          }}
        />
      ) : (
        <div style={{ color: '#333', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
          NO CLIP AVAILABLE
        </div>
      )}

      {/* Bottom gradient for readability */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 260,
        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)',
        pointerEvents: 'none', zIndex: 5,
      }} />

      {/* Metadata + comment preview — bottom left */}
      <div style={{
        position: 'absolute', bottom: 24, left: 16,
        maxWidth: 'calc(100% - 90px)', zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 3,
      }}>
        {/* Recent comments preview */}
        {comments.length > 0 && (
          <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {comments.slice(-3).map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 5, alignItems: 'baseline' }}>
                <span style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '0.78rem', color: '#fff', flexShrink: 0, lineHeight: 1.3 }}>
                  {c.username}
                </span>
                <span style={{ fontFamily: '"Barlow", sans-serif', fontWeight: 300, fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                  {c.body}
                </span>
              </div>
            ))}
            <button
              onClick={() => onOpenComments(clip.clip_id, setComments)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', marginTop: 2 }}
            >
              VIEW ALL {comments.length} COMMENT{comments.length !== 1 ? 'S' : ''}
            </button>
          </div>
        )}

        <div style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 900, fontSize: '1.15rem', color: '#fff', lineHeight: 1.1 }}>
          {clip.broadcaster_name || clip.broadcaster_login || 'BGN Streamer'}
        </div>
        {clip.game_name && (
          <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.55rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {clip.game_name}
          </div>
        )}
        {clip.title && (
          <div style={{ fontFamily: '"Barlow", sans-serif', fontWeight: 300, fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4, marginTop: 1 }}>
            {clip.title}
          </div>
        )}
        {clip.broadcaster_login && (
          <Link
            to={`/profile/${clip.broadcaster_login}`}
            style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.52rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textDecoration: 'none', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <ExternalLink size={9} /> BGN PROFILE
          </Link>
        )}
      </div>

      {/* Action buttons — right side */}
      <div style={{
        position: 'absolute', bottom: 24, right: 12,
        display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', zIndex: 10,
      }}>
        {/* Like */}
        <button
          onClick={handleLike}
          disabled={!authToken || liking}
          title={authToken ? (liked ? 'Unlike' : 'Like') : 'Log in to like'}
          style={{ background: 'none', border: 'none', cursor: authToken ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, opacity: authToken ? 1 : 0.4, transition: 'transform 0.1s', transform: liking ? 'scale(0.9)' : 'scale(1)' }}
        >
          <Heart size={28} fill={liked ? '#ff4444' : 'none'} stroke={liked ? '#ff4444' : '#fff'} strokeWidth={1.5} />
          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.52rem', color: liked ? '#ff4444' : '#fff', letterSpacing: '0.04em', minWidth: 16, textAlign: 'center' }}>
            {likeCount > 0 ? likeCount : ''}
          </span>
        </button>

        {/* Comment */}
        <button
          onClick={() => onOpenComments(clip.clip_id, setComments)}
          title="Comments"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}
        >
          <MessageCircle size={26} stroke="#fff" strokeWidth={1.5} />
          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.52rem', color: '#fff', letterSpacing: '0.04em', minWidth: 16, textAlign: 'center' }}>
            {comments.length > 0 ? comments.length : ''}
          </span>
        </button>

        {/* Share */}
        <button
          onClick={handleShare}
          title="Share"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}
        >
          <Share2 size={24} stroke={shared ? '#888' : '#fff'} strokeWidth={1.5} />
          {shared && (
            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.48rem', color: '#888', letterSpacing: '0.04em' }}>
              COPIED
            </span>
          )}
        </button>
      </div>
    </div>
  );
};


// ── Highlights Page ─────────────────────────────────────────────────────────────

const HighlightsPage = () => {
  const { authToken }                         = useContext(AuthContext);
  const [clips, setClips]                     = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [filterGame, setFilterGame]           = useState('');
  const [filterStreamer, setFilterStreamer]    = useState('');
  const [games, setGames]                     = useState([]);
  const [streamers, setStreamers]             = useState([]);
  const [activeIdx, setActiveIdx]             = useState(0);
  const [openCommentsFor, setOpenCommentsFor] = useState(null); // { clipId, onCommentsChange }
  const containerRef = useRef(null);
  const observerRef  = useRef(null);

  const fetchClips = useCallback((game = '', streamer = '') => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 30 });
    if (game)     params.set('game', game);
    if (streamer) params.set('streamer', streamer);
    fetch(`${BACKEND_URL}/api/highlights/feed?${params}`)
      .then(r => r.json())
      .then(data => {
        const c = data.clips || [];
        setClips(c);
        setGames([...new Set(c.map(x => x.game_name).filter(Boolean))].sort());
        setStreamers([...new Set(c.map(x => x.broadcaster_login).filter(Boolean))].sort());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchClips(); }, [fetchClips]);

  // IntersectionObserver — track which card is in view
  useEffect(() => {
    if (!containerRef.current || clips.length === 0) return;
    observerRef.current?.disconnect();
    const cards = containerRef.current.querySelectorAll('.highlights-card');
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting && e.intersectionRatio >= 0.55) {
            setActiveIdx(Number(e.target.dataset.idx));
          }
        });
      },
      { threshold: 0.55 }
    );
    cards.forEach(c => observerRef.current.observe(c));
    return () => observerRef.current?.disconnect();
  }, [clips]);

  const applyFilter = (game, streamer) => {
    setFilterGame(game);
    setFilterStreamer(streamer);
    fetchClips(game, streamer);
    containerRef.current?.scrollTo({ top: 0 });
    setActiveIdx(0);
  };

  const S = {
    page:   { background: '#000', minHeight: '100vh', color: '#fff', display: 'flex', flexDirection: 'column' },
    bar:    { position: 'sticky', top: 0, zIndex: 40, background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #111', padding: '0.6rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
    title:  { fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 900, fontSize: '1.2rem', letterSpacing: '-0.01em', color: '#fff', flexShrink: 0 },
    select: { background: 'transparent', border: '1px solid #222', color: '#666', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.58rem', letterSpacing: '0.08em', padding: '0.3rem 0.65rem', outline: 'none', cursor: 'pointer', textTransform: 'uppercase' },
    feed:   { flex: 1, overflowY: 'scroll', scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch' },
    empty:  { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.65rem', color: '#2a2a2a', letterSpacing: '0.15em' },
  };

  return (
    <div style={S.page}>
      {/* Filter bar */}
      <div style={S.bar}>
        <span style={S.title}>BGN HIGHLIGHTS</span>
        <select value={filterGame} onChange={e => applyFilter(e.target.value, filterStreamer)} style={S.select}>
          <option value="">ALL GAMES</option>
          {games.map(g => <option key={g} value={g}>{g.toUpperCase()}</option>)}
        </select>
        <select value={filterStreamer} onChange={e => applyFilter(filterGame, e.target.value)} style={S.select}>
          <option value="">ALL STREAMERS</option>
          {streamers.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
        {(filterGame || filterStreamer) && (
          <button onClick={() => applyFilter('', '')} style={{ ...S.select, cursor: 'pointer', color: '#444' }}>
            CLEAR
          </button>
        )}
      </div>

      {/* Feed */}
      <div ref={containerRef} style={S.feed}>
        {loading  && <div style={S.empty}>LOADING HIGHLIGHTS...</div>}
        {!loading && clips.length === 0 && <div style={S.empty}>NO HIGHLIGHTS YET</div>}
        {!loading && clips.map((clip, i) => (
          <div key={clip.clip_id || i} className="highlights-card" data-idx={i}>
            <ClipCard
              clip={clip}
              isActive={i === activeIdx}
              authToken={authToken}
              onOpenComments={(clipId, onCommentsChange) => setOpenCommentsFor({ clipId, onCommentsChange })}
            />
          </div>
        ))}
      </div>

      {/* Comment drawer */}
      {openCommentsFor && (
        <CommentDrawer
          clipId={openCommentsFor.clipId}
          authToken={authToken}
          onClose={() => setOpenCommentsFor(null)}
          onCommentsChange={openCommentsFor.onCommentsChange}
        />
      )}
    </div>
  );
};

export default HighlightsPage;
